import React, { useState, useEffect } from "react";
import { Box, Button, Typography, TextField } from "@mui/material";
import { DistinctValue } from "../../../types";
import { useDialogs } from "../../../../dialog/use-dialog";
import { useNotification } from "../../../../../context/notification-provider";
import { changeValues } from "../../../services/cleaning-data-service";

interface ChangeValuesFooterProps {
  selectedItem: DistinctValue | null;
  fieldId: number | undefined;
  refreshDistinctValues: () => void;
}

/**
 * Footer for Change Values category (categoryId: 4)
 */
const ChangeValuesFooter: React.FC<ChangeValuesFooterProps> = ({
  selectedItem,
  fieldId,
  refreshDistinctValues,
}) => {
  const { confirm } = useDialogs();
  const { showNotification } = useNotification();
  const [newValue, setNewValue] = useState("");

  // Update newValue when selectedItem changes
  useEffect(() => {
    if (selectedItem) {
      setNewValue(selectedItem.value);
    } else {
      setNewValue("");
    }
  }, [selectedItem]);

  const handleApplyChanges = async () => {
    if (!selectedItem) {
      showNotification({
        message: "No item selected.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    if (fieldId === undefined) {
      showNotification({
        message: "No field selected.",
        severity: "error",
        duration: 3000,
      });
      return;
    }

    if (!newValue.trim()) {
      showNotification({
        message: "New value cannot be empty.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    if (newValue === selectedItem.value) {
      showNotification({
        message: "New value is the same as the current value.",
        severity: "info",
        duration: 3000,
      });
      return;
    }

    try {
      const result = await confirm(
        `Are you sure you want to change "${selectedItem.value}" to "${newValue}"?`,
        {
          title: "Confirm Value Change",
          okText: "Apply Changes",
          cancelText: "Cancel",
          severity: "success",
        },
      );

      if (result === true) {
        try {
          await changeValues(fieldId, [
            {
              value: selectedItem.value,
              newValue: newValue.trim(),
            },
          ]);
          refreshDistinctValues();
          showNotification({
            message: "Value changed successfully.",
            severity: "success",
            duration: 3000,
          });
          setNewValue("");
        } catch (err) {
          showNotification({
            message: `Failed to change value. Reason: ${(err as Error).message}`,
            severity: "error",
            duration: 3000,
          });
        }
      }
    } catch {
      showNotification({
        message: "Failed to show confirmation dialog.",
        severity: "error",
        duration: 3000,
      });
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {selectedItem
            ? `Change the value "${selectedItem.value}" to a new value:`
            : "Click on a value in the list to select it for changes."}
        </Typography>
        {selectedItem && (
          <TextField
            label="New Value"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            size="small"
            fullWidth
            placeholder="Enter new value"
            disabled={!selectedItem}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ mr: 1, borderRadius: 1 }}
          onClick={handleApplyChanges}
          disabled={!selectedItem || !newValue.trim()}
        >
          Apply Changes
        </Button>
      </Box>
    </Box>
  );
};

export default ChangeValuesFooter;
